// Gyroscope : direction visée par le dos du téléphone (là où regarderait son appareil photo), à
// partir des angles de DeviceOrientationEvent. Fonctions pures, sans DOM.
//
// Les angles W3C (alpha autour de Z, beta autour de X, gamma autour de Y, rotations intrinsèques
// Z-X'-Y'') donnent la matrice R = Rz(alpha)·Rx(beta)·Ry(gamma), qui exprime les axes du téléphone
// dans le repère terrestre (x Est, y Nord, z vers le haut). Le dos du téléphone est son axe -Z :
// sa direction terrestre est l'opposé de la 3e colonne de R. On en tire un cap (yaw, comme une
// boussole : 0 = référence, croît vers la droite) et une inclinaison (pitch, 0 = horizon, > 0 = ciel).
// Passer par ce vecteur évite le blocage de cardan des angles bruts quand le téléphone est tenu
// debout (beta ≈ 90°) — la position naturelle pour viser — et rend le résultat indépendant de la
// rotation de l'écran (portrait/paysage).

import { GYRO_LOWERED_PITCH } from '../config/constants.js';

const DEG = Math.PI / 180;

/**
 * @param {number} alpha  degrés (0–360)
 * @param {number} beta   degrés (-180–180)
 * @param {number} gamma  degrés (-90–90)
 * @returns {{yaw:number, pitch:number}|null}  degrés ; null si une mesure manque
 */
export function viewDirection(alpha, beta, gamma) {
  if (![alpha, beta, gamma].every(Number.isFinite)) return null;
  const sZ = Math.sin(alpha * DEG), cZ = Math.cos(alpha * DEG);
  const sX = Math.sin(beta * DEG), cX = Math.cos(beta * DEG);
  const sY = Math.sin(gamma * DEG), cY = Math.cos(gamma * DEG);
  // Dos du téléphone = -(3e colonne de R)
  const east = -(cY * sZ * sX + cZ * sY);
  const north = -(sZ * sY - cZ * cY * sX);
  const up = -(cX * cY);
  return {
    yaw: Math.atan2(east, north) / DEG,
    pitch: Math.asin(Math.max(-1, Math.min(1, up))) / DEG,
  };
}

/**
 * Le téléphone est-il baissé au point de quitter le gyroscope ? (inclinaison en degrés, 0 = horizon, < 0 = vers le sol)
 * @param {number} pitch
 * @param {number} [limit]  par défaut GYRO_LOWERED_PITCH (-25°)
 */
export const isPhoneLowered = (pitch, limit = GYRO_LOWERED_PITCH) => Number.isFinite(pitch) && pitch <= limit;

/** Plus court écart signé entre deux angles, dans [-180, 180). */
export function angleDelta(from, to) {
  return ((((to - from) % 360) + 540) % 360) - 180;
}

/**
 * Déplacement de la caméra (unités monde) entre deux directions visées : un tour complet sur soi
 * parcourt toute la largeur du monde (qui boucle, voir core/camera.js#wrapX) ; en hauteur, viser
 * `pitchRange` degrés au-dessus (ou au-dessous) de l'horizon suffit à atteindre le bord du monde.
 * Près de la verticale (|pitch| > maxPitch), le cap devient instable : on n'en tient pas compte.
 * @param {{yaw:number,pitch:number}} prev
 * @param {{yaw:number,pitch:number}} next
 * @param {object} opts
 * @param {number} opts.worldSize
 * @param {number} opts.panLimit    jusqu'où la caméra peut aller en Y (voir getPanLimit)
 * @param {number} opts.pitchRange  degrés pour aller de l'horizon au bord du monde en Y
 * @param {number} [opts.maxPitch]  au-delà, le cap est ignoré
 * @param {number} [opts.yawSpeed]  multiplicateur du défilement horizontal (1 = un tour sur soi = tout le monde)
 * @returns {{dx:number, dy:number}}
 */
export function gyroPanDelta(prev, next, { worldSize, panLimit, pitchRange, maxPitch = 75, yawSpeed = 1 }) {
  if (!prev || !next || !(worldSize > 0)) return { dx: 0, dy: 0 };
  const perDegX = worldSize / 360;
  // Jamais moins sensible en hauteur qu'en largeur : le décor ne doit pas « traîner » verticalement
  const perDegY = Math.max(perDegX, pitchRange > 0 ? panLimit / pitchRange : 0);
  const steady = Math.abs(prev.pitch) <= maxPitch && Math.abs(next.pitch) <= maxPitch;
  return {
    dx: steady ? angleDelta(prev.yaw, next.yaw) * perDegX * yawSpeed : 0,
    dy: -(next.pitch - prev.pitch) * perDegY, // viser plus haut = caméra vers le haut (y négatif)
  };
}

/**
 * Hauteur de la caméra (unités monde) pour une inclinaison donnée du téléphone : ABSOLUE, pas relative.
 * Téléphone tenu à l'horizontale, dos vers l'horizon réel (pitch 0), l'horizon du décor est au centre
 * du viseur (y = 0) ; viser plus haut (pitch > 0) monte vers le ciel (y négatif), plus bas descend
 * vers le sol. `pitchRange` degrés mènent au bord du monde, comme pour gyroPanDelta.
 * @param {number} pitch  degrés, 0 = horizon
 * @param {object} opts
 * @param {number} opts.worldSize
 * @param {number} opts.panLimit   jusqu'où la caméra peut aller en Y (voir getPanLimit)
 * @param {number} opts.pitchRange degrés pour aller de l'horizon au bord du monde en Y
 * @returns {number}  non borné : l'appelant le ramène dans [-panLimit, panLimit]
 */
export function gyroVerticalPosition(pitch, { worldSize, panLimit, pitchRange }) {
  if (!Number.isFinite(pitch) || !(worldSize > 0)) return 0;
  return -pitch * gyroDegreesToY({ worldSize, panLimit, pitchRange }) || 0; // || 0 : jamais -0
}

/** Unités monde par degré d'inclinaison (jamais moins sensible qu'en largeur, voir gyroPanDelta). */
function gyroDegreesToY({ worldSize, panLimit, pitchRange }) {
  return Math.max(worldSize / 360, pitchRange > 0 ? panLimit / pitchRange : 0);
}

/**
 * Lisse les mesures du capteur (filtre exponentiel) : renvoie la direction filtrée qui se rapproche
 * de `raw` de la fraction `1 - smoothing` à chaque mesure. Le cap passe par le plus court chemin
 * (0°/360°). `smoothing` de 0 (brut) à 0,95 ; sans direction précédente, la mesure est reprise telle quelle.
 * @param {{yaw:number,pitch:number}|null} prev  direction filtrée précédente
 * @param {{yaw:number,pitch:number}} raw
 * @param {number} smoothing  0..1
 */
export function smoothDirection(prev, raw, smoothing) {
  if (!prev) return raw;
  const keep = Math.max(0, Math.min(0.95, smoothing || 0));
  const k = 1 - keep;
  return {
    yaw: prev.yaw + angleDelta(prev.yaw, raw.yaw) * k,
    pitch: prev.pitch + (raw.pitch - prev.pitch) * k,
  };
}

